/**
 * Widget Data Service
 * 
 * Provides data synchronization between the React app and native
 * home screen widgets (iOS WidgetKit, Android App Widgets).
 * 
 * The actual widget UI must be built natively (SwiftUI/Kotlin), but
 * this service handles:
 * - Syncing appointment data to shared storage
 * - Triggering widget refreshes
 * - Managing widget state
 * 
 * Uses capacitor-widget-bridge for data synchronization.
 */

import { Capacitor } from '@capacitor/core';

// Widget data keys
const WIDGET_DATA_KEYS = {
  UPCOMING_APPOINTMENTS: 'upcoming_appointments',
  TODAY_MEDICATIONS: 'today_medications',
  QUICK_ACCESS: 'quick_access',
  LAST_UPDATED: 'widget_last_updated',
};

// Widget types
export const WIDGET_TYPES = {
  APPOINTMENTS: 'appointments',
  MEDICATIONS: 'medications',
  QUICK_ACCESS: 'quickAccess',
};

// Dynamic import for widget bridge
let WidgetBridge = null;

/**
 * Initialize widget bridge plugin
 */
const initWidgetBridge = async () => {
  if (WidgetBridge) return true;
  
  if (!Capacitor.isNativePlatform()) {
    console.log('[Widget] Web platform - using localStorage fallback');
    return false;
  }
  
  try {
    // Try to load the widget bridge plugin
    const module = await import('capacitor-widget-bridge');
    WidgetBridge = module.WidgetBridgePlugin;
    
    // Register widget classes (Android requirement)
    if (Capacitor.getPlatform() === 'android') {
      await WidgetBridge.setRegisteredWidgets({
        widgets: [
          'com.everydayworks.forgetmenot.AppointmentsWidget',
          'com.everydayworks.forgetmenot.MedicationsWidget',
          'com.everydayworks.forgetmenot.QuickAccessWidget',
        ],
      });
    }
    
    return true;
  } catch (error) {
    console.warn('[Widget] Widget bridge not available:', error.message);
    return false;
  }
};

/**
 * Set data in widget shared storage
 */
const setWidgetData = async (key, data) => {
  const isNative = await initWidgetBridge();
  
  const jsonData = JSON.stringify(data);
  
  if (isNative && WidgetBridge) {
    try {
      await WidgetBridge.setItem({
        key,
        value: jsonData,
        group: 'group.com.everydayworks.forgetmenot', // iOS App Group
      });
      return { success: true };
    } catch (error) {
      console.error('[Widget] Failed to set data:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  localStorage.setItem(`widget_${key}`, jsonData);
  return { success: true, webFallback: true };
};

/**
 * Get data from widget shared storage
 */
const getWidgetData = async (key) => {
  const isNative = await initWidgetBridge();
  
  if (isNative && WidgetBridge) {
    try {
      const result = await WidgetBridge.getItem({
        key,
        group: 'group.com.everydayworks.forgetmenot',
      });
      return result.value ? JSON.parse(result.value) : null;
    } catch (error) {
      console.error('[Widget] Failed to get data:', error);
      return null;
    }
  }
  
  // Web fallback
  const data = localStorage.getItem(`widget_${key}`);
  return data ? JSON.parse(data) : null;
};

/**
 * Trigger widget refresh
 */
const refreshWidgets = async (widgetType = null) => {
  const isNative = await initWidgetBridge();
  
  if (!isNative || !WidgetBridge) {
    return { success: false, error: 'Widgets not available' };
  }
  
  try {
    if (widgetType) {
      // Refresh specific widget timeline
      await WidgetBridge.reloadTimelines({
        ofKind: widgetType,
      });
    } else {
      // Refresh all widgets
      await WidgetBridge.reloadAllTimelines();
    }
    return { success: true };
  } catch (error) {
    console.error('[Widget] Failed to refresh:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sync upcoming appointments to widget
 * 
 * @param {Array} appointments - Array of appointment objects
 */
export const syncAppointmentsToWidget = async (appointments) => {
  // Format appointments for widget display
  const widgetData = appointments
    .filter(apt => apt.status !== 'completed')
    .slice(0, 5) // Limit to 5 for widget
    .map(apt => ({
      id: apt.id,
      doctorName: apt.doctorName || 'Doctor',
      specialty: apt.specialty || '',
      date: apt.appointmentDate,
      time: apt.appointmentTime || '',
      reason: apt.reason || '',
      status: apt.status,
    }));
  
  const result = await setWidgetData(WIDGET_DATA_KEYS.UPCOMING_APPOINTMENTS, {
    appointments: widgetData,
    updatedAt: new Date().toISOString(),
  });
  
  if (result.success) {
    await refreshWidgets(WIDGET_TYPES.APPOINTMENTS);
  }
  
  return result;
};

/**
 * Sync today's medications to widget
 * 
 * @param {Array} medications - Array of medication objects with reminder times
 */
export const syncMedicationsToWidget = async (medications) => {
  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  
  // Get today's adherence data
  const todayKey = now.toISOString().split('T')[0];
  const adherence = JSON.parse(localStorage.getItem(`med_taken_${todayKey}`) || '{}');
  
  // Format medications for widget
  const widgetData = medications
    .filter(med => med.is_active && med.reminderTimes?.length > 0)
    .flatMap(med => 
      (med.reminderTimes || []).map(time => ({
        id: `${med.id}_${time}`,
        medicationId: med.id,
        name: med.name,
        dosage: med.dosage || '',
        time,
        taken: adherence[med.id]?.includes(time) || false,
        isPast: time < currentTime,
      }))
    )
    .sort((a, b) => a.time.localeCompare(b.time));
  
  const result = await setWidgetData(WIDGET_DATA_KEYS.TODAY_MEDICATIONS, {
    medications: widgetData,
    date: todayKey,
    updatedAt: new Date().toISOString(),
  });
  
  if (result.success) {
    await refreshWidgets(WIDGET_TYPES.MEDICATIONS);
  }
  
  return result;
};

/**
 * Sync quick access items to widget
 * 
 * @param {Object} quickAccess - Quick access configuration
 */
export const syncQuickAccessToWidget = async (quickAccess) => {
  const widgetData = {
    emergencyContacts: (quickAccess.emergencyContacts || []).slice(0, 3).map(c => ({
      name: c.name,
      phone: c.phone,
      relationship: c.relationship,
    })),
    bloodType: quickAccess.bloodType || '',
    allergies: (quickAccess.allergies || []).slice(0, 5).map(a => a.name || a.allergen),
    conditions: (quickAccess.conditions || []).slice(0, 5).map(c => c.condition || c.name),
    updatedAt: new Date().toISOString(),
  };
  
  const result = await setWidgetData(WIDGET_DATA_KEYS.QUICK_ACCESS, widgetData);
  
  if (result.success) {
    await refreshWidgets(WIDGET_TYPES.QUICK_ACCESS);
  }
  
  return result;
};

/**
 * Sync all widget data
 * 
 * @param {Object} data - All data to sync
 */
export const syncAllWidgetData = async (data) => {
  const results = {
    appointments: null,
    medications: null,
    quickAccess: null,
  };
  
  if (data.appointments) {
    results.appointments = await syncAppointmentsToWidget(data.appointments);
  }
  
  if (data.medications) {
    results.medications = await syncMedicationsToWidget(data.medications);
  }
  
  if (data.quickAccess) {
    results.quickAccess = await syncQuickAccessToWidget(data.quickAccess);
  }
  
  // Update last sync time
  await setWidgetData(WIDGET_DATA_KEYS.LAST_UPDATED, {
    timestamp: new Date().toISOString(),
  });
  
  return results;
};

/**
 * Request widget installation (Android only)
 * Shows the system widget picker for the user
 */
export const requestWidgetInstall = async (widgetType) => {
  const isNative = await initWidgetBridge();
  
  if (!isNative || !WidgetBridge) {
    return { success: false, error: 'Widgets not available on this platform' };
  }
  
  if (Capacitor.getPlatform() !== 'android') {
    return { success: false, error: 'Widget pinning only available on Android' };
  }
  
  try {
    const widgetClass = {
      [WIDGET_TYPES.APPOINTMENTS]: 'com.everydayworks.forgetmenot.AppointmentsWidget',
      [WIDGET_TYPES.MEDICATIONS]: 'com.everydayworks.forgetmenot.MedicationsWidget',
      [WIDGET_TYPES.QUICK_ACCESS]: 'com.everydayworks.forgetmenot.QuickAccessWidget',
    }[widgetType];
    
    if (!widgetClass) {
      return { success: false, error: 'Unknown widget type' };
    }
    
    await WidgetBridge.requestWidget({
      widgetClass,
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Check if widgets are supported on this device
 */
export const isWidgetSupported = async () => {
  if (!Capacitor.isNativePlatform()) {
    return { supported: false, reason: 'Web platform' };
  }
  
  const platform = Capacitor.getPlatform();
  
  // iOS 14+ supports WidgetKit
  // Android 4.0+ supports App Widgets
  return {
    supported: true,
    platform,
    types: [
      { type: WIDGET_TYPES.APPOINTMENTS, name: 'Upcoming Appointments', description: 'Shows your next appointments' },
      { type: WIDGET_TYPES.MEDICATIONS, name: 'Today\'s Medications', description: 'Track your daily medication schedule' },
      { type: WIDGET_TYPES.QUICK_ACCESS, name: 'Quick Access', description: 'Emergency contacts and vital info' },
    ],
  };
};

/**
 * Widget installation instructions
 */
export const getWidgetInstructions = () => {
  const platform = Capacitor.getPlatform();
  
  if (platform === 'ios') {
    return {
      title: 'Add Widget on iPhone',
      steps: [
        'Long press on your home screen until apps start wiggling',
        'Tap the + button in the top left corner',
        'Search for "Forget Me Not"',
        'Choose a widget size and tap "Add Widget"',
        'Position the widget where you want it',
        'Tap "Done" to finish',
      ],
    };
  }
  
  if (platform === 'android') {
    return {
      title: 'Add Widget on Android',
      steps: [
        'Long press on an empty area of your home screen',
        'Tap "Widgets"',
        'Find "Forget Me Not" in the list',
        'Long press the widget you want and drag it to your home screen',
        'Resize if needed by dragging the edges',
      ],
    };
  }
  
  return {
    title: 'Widgets',
    steps: ['Widgets are only available in the mobile app'],
  };
};

export default {
  syncAppointmentsToWidget,
  syncMedicationsToWidget,
  syncQuickAccessToWidget,
  syncAllWidgetData,
  requestWidgetInstall,
  isWidgetSupported,
  getWidgetInstructions,
  WIDGET_TYPES,
};
